export interface EmptyStateProps {
  /** e.g. t('ec2.empty.quests.title'). */
  title: string;
  /** e.g. t('ec2.empty.quests.body'). */
  body?: string;
  /**
   * Accent color of the single highlighted module in the illustration
   * (a category color or gold; defaults to Beam/accent).
   */
  accentClassName?: string;
  className?: string;
}

/**
 * Empty state (brief §4 "Illustration"): an "exploded QR code" — rounded squares
 * in 2–3 sizes plus one finder eye in --fg-muted, with a single accent element.
 * No mascots, no outlines, no gradients.
 */
export function EmptyState({ title, body, accentClassName = 'text-accent', className = '' }: EmptyStateProps) {
  return (
    <div data-testid="ec-empty-state" className={`flex flex-col items-center gap-4 px-6 py-12 text-center ${className}`}>
      <svg viewBox="0 0 120 96" width="120" height="96" aria-hidden className="text-fg-muted">
        {/* exploded QR modules (flat fills, 3 sizes) */}
        <rect x="14" y="18" width="14" height="14" rx="4" fill="currentColor" opacity="0.5" />
        <rect x="40" y="8" width="9" height="9" rx="3" fill="currentColor" opacity="0.7" />
        <rect x="88" y="14" width="14" height="14" rx="4" fill="currentColor" opacity="0.45" />
        <rect x="104" y="42" width="9" height="9" rx="3" fill="currentColor" opacity="0.6" />
        <rect x="16" y="62" width="9" height="9" rx="3" fill="currentColor" opacity="0.6" />
        <rect x="36" y="76" width="14" height="14" rx="4" fill="currentColor" opacity="0.5" />
        <rect x="86" y="70" width="9" height="9" rx="3" fill="currentColor" opacity="0.7" />
        <rect x="66" y="8" width="6" height="6" rx="2" fill="currentColor" opacity="0.55" />
        <rect x="6" y="42" width="6" height="6" rx="2" fill="currentColor" opacity="0.55" />
        {/* the finder eye — the single accent element */}
        <g className={accentClassName}>
          <rect x="48" y="34" width="26" height="26" rx="7" fill="none" stroke="currentColor" strokeWidth="5" />
          <rect x="56" y="42" width="10" height="10" rx="3" fill="currentColor" />
        </g>
      </svg>
      <h3 className="text-[17px] font-semibold text-fg">{title}</h3>
      {body && <p className="max-w-[32ch] text-[13px] font-medium text-fg-secondary">{body}</p>}
    </div>
  );
}
